import { useState } from 'react';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import { useAuditLog } from '@/hooks/useAdmin';
import { AuditLogEntry } from '@/schema/admin';
import { Column, DataTable } from '../components/DataTable';
import { SectionCard } from '../components/SectionCard';
import { EM_DASH, formatDateTime } from '../lib/format';

const PAGE_SIZE = 20;

export function AdminAuditLog() {
  useDocumentTitle('Admin - Audit log');
  const [page, setPage] = useState(0);
  const { data, isLoading } = useAuditLog(page, PAGE_SIZE);

  const columns: Column<AuditLogEntry>[] = [
    {
      key: 'when',
      header: 'When',
      render: (row) => <span className="text-sm text-white/70">{formatDateTime(row.createdAt)}</span>,
    },
    {
      key: 'actor',
      header: 'Who',
      render: (row) => <span className="text-sm text-white/85">{row.actorEmail ?? 'System'}</span>,
    },
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <span className="rounded-full border border-white/12 bg-white/5 px-2.5 py-0.5 text-xs text-white/75">
          {row.action}
        </span>
      ),
    },
    {
      key: 'target',
      header: 'Target',
      render: (row) => (
        <div className="min-w-0">
          <p className="truncate text-sm text-white/85">{row.targetLabel ?? EM_DASH}</p>
          <p className="truncate text-xs text-white/40">{row.targetType ?? EM_DASH}</p>
        </div>
      ),
    },
    {
      key: 'detail',
      header: 'Detail',
      render: (row) => <span className="text-sm text-white/60">{row.detail ?? EM_DASH}</span>,
    },
  ];

  return (
    <SectionCard
      title="Audit log"
      description="Administrative actions, kept so a change stays traceable after the record is gone."
    >
      <DataTable
        columns={columns}
        rows={data?.content ?? []}
        rowKey={(row) => row.id}
        page={page}
        totalPages={data?.totalPages ?? 0}
        onPageChange={setPage}
        isLoading={isLoading}
        emptyMessage="Nothing recorded yet."
      />
    </SectionCard>
  );
}

export default AdminAuditLog;
